import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import BulkImportDialog from "@/components/inventory/BulkImportDialog";
import ItemPicker from "@/components/inventory/ItemPicker";
import {
  ArrowLeft, Package, Plus, Pencil, Trash, MagnifyingGlass, Warning, Truck, Storefront, ShoppingCart,
  UploadSimple, PaperPlaneTilt, ChartBar, ClipboardText, FilePdf,
} from "@phosphor-icons/react";

const emptyItem = { code: "", name: "", category: "", unit: "PCS", description: "", moq: 0, rol: 0, opening_stock: 0, unit_price: 0, hsn_sac: "", gst_rate: 0, is_active: true };
const emptyVendor = { name: "", contact_person: "", email: "", phone: "", address: "", gstin: "", pan: "", bank_name: "", bank_account: "", bank_ifsc: "", payment_terms: "", categories: [], notes: "", is_active: true };
const emptyMR = { department: "", required_by: "", purpose: "", lines: [] };
const DEFAULT_DELIVERY_ADDRESS = "CM Envirosystems, 18,19 Dobaspet Industrial Area, Bengaluru - 562111";
const emptyPO = { vendor_id: "", expected_date: "", delivery_address: DEFAULT_DELIVERY_ADDRESS, payment_terms: "", notes: "", lines: [] };
const emptyInward = { po_id: "", vendor_id: "", invoice_no: "", invoice_date: "", notes: "", lines: [] };
const emptyIssue = { mr_id: "", issued_to_user_id: "", department: "", purpose: "", notes: "", lines: [] };

// HTML-escape user data before injecting into a document.write payload.
const escHtml = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

const filt = (arr, q, fields) => {
  const qq = q.trim().toLowerCase();
  if (!qq) return arr;
  return arr.filter((r) => fields.some((f) => String(r[f] ?? "").toLowerCase().includes(qq)));
};

export default function InventoryHome() {
  const navigate = useNavigate();
  const { user, canEditInventory } = useAuth();

  const [activeTab, setActiveTab] = useState("dashboard");
  const [loading, setLoading] = useState(true);

  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [invDepartments, setInvDepartments] = useState([]);
  const [mrs, setMrs] = useState([]);
  const [pos, setPos] = useState([]);
  const [inward, setInward] = useState([]);
  const [issuance, setIssuance] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [reports, setReports] = useState({ spend: null, vendor: null, lowStock: null });

  const [itemsSearch, setItemsSearch] = useState("");
  const [vendorsSearch, setVendorsSearch] = useState("");

  const [itemDialog, setItemDialog] = useState(false);
  const [itemForm, setItemForm] = useState(emptyItem);
  const [editItemId, setEditItemId] = useState(null);

  const [vendorDialog, setVendorDialog] = useState(false);
  const [vendorForm, setVendorForm] = useState(emptyVendor);
  const [editVendorId, setEditVendorId] = useState(null);

  const [catDialog, setCatDialog] = useState(false);
  const [catForm, setCatForm] = useState({ name: "", description: "", is_active: true });
  const [editCatId, setEditCatId] = useState(null);

  const [uomDialog, setUomDialog] = useState(false);
  const [uomForm, setUomForm] = useState({ code: "", name: "", is_active: true });
  const [editUomId, setEditUomId] = useState(null);

  const [deptDialog, setDeptDialog] = useState(false);
  const [deptForm, setDeptForm] = useState({ name: "", description: "", is_active: true });
  const [editDeptId, setEditDeptId] = useState(null);

  const [reports2, setReports2] = useState({ stock: null });

  const [bulkItemsOpen, setBulkItemsOpen] = useState(false);
  const [bulkVendorsOpen, setBulkVendorsOpen] = useState(false);

  const [mrDialog, setMrDialog] = useState(false);
  const [mrForm, setMrForm] = useState(emptyMR);

  const [poDialog, setPoDialog] = useState(false);
  const [poForm, setPoForm] = useState(emptyPO);

  const [inwardDialog, setInwardDialog] = useState(false);
  const [inwardForm, setInwardForm] = useState(emptyInward);
  const [inwardPoLines, setInwardPoLines] = useState([]);

  const [issueDialog, setIssueDialog] = useState(false);
  const [issueForm, setIssueForm] = useState(emptyIssue);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sum, it, vn, ca, uo, dpt, mr, po, inw, iss, us] = await Promise.all([
        axios.get(`${API}/inventory/summary`, { withCredentials: true }),
        axios.get(`${API}/inventory/items`, { withCredentials: true }),
        axios.get(`${API}/inventory/vendors`, { withCredentials: true }),
        axios.get(`${API}/inventory/categories`, { withCredentials: true }),
        axios.get(`${API}/inventory/uoms`, { withCredentials: true }),
        axios.get(`${API}/inventory/departments`, { withCredentials: true }),
        axios.get(`${API}/inventory/requisitions`, { withCredentials: true }),
        axios.get(`${API}/inventory/purchase-orders`, { withCredentials: true }),
        axios.get(`${API}/inventory/inward`, { withCredentials: true }),
        axios.get(`${API}/inventory/issuance`, { withCredentials: true }),
        axios.get(`${API}/users`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setSummary(sum.data); setItems(it.data); setVendors(vn.data);
      setCategories(ca.data); setUoms(uo.data); setInvDepartments(dpt.data);
      setMrs(mr.data); setPos(po.data); setInward(inw.data); setIssuance(iss.data);
      setAllUsers(us.data || []);
    } catch (err) {
      if (err.response?.status === 403) { toast.error("No access to inventory module"); navigate("/"); }
      else toast.error(err.response?.data?.detail || "Failed to load inventory data");
    } finally { setLoading(false); }
  }, [navigate]);

  const loadReports = useCallback(async () => {
    try {
      const [s, v, l, st] = await Promise.all([
        axios.get(`${API}/inventory/reports/spend-by-category`, { withCredentials: true }),
        axios.get(`${API}/inventory/reports/vendor-performance`, { withCredentials: true }),
        axios.get(`${API}/inventory/reports/low-stock`, { withCredentials: true }),
        axios.get(`${API}/inventory/reports/stock-summary`, { withCredentials: true }),
      ]);
      setReports({ spend: s.data, vendor: v.data, lowStock: l.data });
      setReports2({ stock: st.data });
    } catch { toast.error("Failed to load reports"); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (activeTab === "reports") loadReports(); }, [activeTab, loadReports]);

  const filteredItems = useMemo(() => filt(items, itemsSearch, ["code", "name", "category", "unit"]), [items, itemsSearch]);
  const filteredVendors = useMemo(() => filt(vendors, vendorsSearch, ["name", "email", "phone", "contact_person"]), [vendors, vendorsSearch]);

  const openNewItem = () => { setEditItemId(null); setItemForm(emptyItem); setItemDialog(true); };
  const openEditItem = (it) => { setEditItemId(it.item_id); setItemForm({ ...emptyItem, ...it }); setItemDialog(true); };
  const saveItem = async () => {
    if (!itemForm.code.trim() || !itemForm.name.trim()) return toast.error("Code and name are required");
    try {
      if (editItemId) await axios.patch(`${API}/inventory/items/${editItemId}`, itemForm, { withCredentials: true });
      else await axios.post(`${API}/inventory/items`, itemForm, { withCredentials: true });
      toast.success("Item saved"); setItemDialog(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };
  const deleteItem = async (it) => { if (!window.confirm(`Delete "${it.name}"?`)) return; try { await axios.delete(`${API}/inventory/items/${it.item_id}`, { withCredentials: true }); load(); } catch (err) { toast.error("Delete failed"); } };

  const openNewVendor = () => { setEditVendorId(null); setVendorForm(emptyVendor); setVendorDialog(true); };
  const openEditVendor = (v) => { setEditVendorId(v.vendor_id); setVendorForm({ ...emptyVendor, ...v, categories: v.categories || [] }); setVendorDialog(true); };
  const saveVendor = async () => {
    if (!vendorForm.name.trim()) return toast.error("Vendor name is required");
    try {
      if (editVendorId) await axios.patch(`${API}/inventory/vendors/${editVendorId}`, vendorForm, { withCredentials: true });
      else await axios.post(`${API}/inventory/vendors`, vendorForm, { withCredentials: true });
      toast.success("Vendor saved"); setVendorDialog(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };
  const deleteVendor = async (v) => { if (!window.confirm(`Delete "${v.name}"?`)) return; try { await axios.delete(`${API}/inventory/vendors/${v.vendor_id}`, { withCredentials: true }); load(); } catch { toast.error("Delete failed"); } };

  const openNewCat = () => { setEditCatId(null); setCatForm({ name: "", description: "", is_active: true }); setCatDialog(true); };
  const openEditCat = (c) => { setEditCatId(c.category_id); setCatForm({ name: c.name, description: c.description || "", is_active: c.is_active }); setCatDialog(true); };
  const saveCat = async () => {
    if (!catForm.name.trim()) return toast.error("Name required");
    try {
      if (editCatId) await axios.patch(`${API}/inventory/categories/${editCatId}`, catForm, { withCredentials: true });
      else await axios.post(`${API}/inventory/categories`, catForm, { withCredentials: true });
      setCatDialog(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };
  const deleteCat = async (c) => { if (!window.confirm(`Delete "${c.name}"?`)) return; await axios.delete(`${API}/inventory/categories/${c.category_id}`, { withCredentials: true }); load(); };

  const openNewUom = () => { setEditUomId(null); setUomForm({ code: "", name: "", is_active: true }); setUomDialog(true); };
  const openEditUom = (u) => { setEditUomId(u.uom_id); setUomForm({ code: u.code, name: u.name, is_active: u.is_active }); setUomDialog(true); };
  const saveUom = async () => {
    if (!uomForm.code.trim() || !uomForm.name.trim()) return toast.error("Code and name required");
    try {
      if (editUomId) await axios.patch(`${API}/inventory/uoms/${editUomId}`, uomForm, { withCredentials: true });
      else await axios.post(`${API}/inventory/uoms`, uomForm, { withCredentials: true });
      setUomDialog(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };
  const deleteUom = async (u) => { if (!window.confirm(`Delete "${u.code}"?`)) return; await axios.delete(`${API}/inventory/uoms/${u.uom_id}`, { withCredentials: true }); load(); };

  const openNewDept = () => { setEditDeptId(null); setDeptForm({ name: "", description: "", is_active: true }); setDeptDialog(true); };
  const openEditDept = (d) => { setEditDeptId(d.department_id); setDeptForm({ name: d.name, description: d.description || "", is_active: d.is_active }); setDeptDialog(true); };
  const saveDept = async () => {
    if (!deptForm.name.trim()) return toast.error("Name required");
    try {
      if (editDeptId) await axios.patch(`${API}/inventory/departments/${editDeptId}`, deptForm, { withCredentials: true });
      else await axios.post(`${API}/inventory/departments`, deptForm, { withCredentials: true });
      setDeptDialog(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };
  const deleteDept = async (d) => { if (!window.confirm(`Delete "${d.name}"?`)) return; await axios.delete(`${API}/inventory/departments/${d.department_id}`, { withCredentials: true }); load(); };

  const [autoMrPending, setAutoMrPending] = useState(false);
  const autoCreateMRFromLowStock = async () => {
    if (autoMrPending) return;
    setAutoMrPending(true);
    try {
      const res = await axios.post(`${API}/inventory/requisitions/auto-from-low-stock`, {}, { withCredentials: true });
      toast.success(res.data.message);
      setActiveTab("mrs");
      load();
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error(err.response.data.detail || "Auto-requisition already open");
        setActiveTab("mrs");
      } else {
        toast.error(err.response?.data?.detail || "Auto-MR failed");
      }
    } finally {
      setAutoMrPending(false);
    }
  };

  const openNewMR = () => { setMrForm({ ...emptyMR, department: user?.department || "" }); setMrDialog(true); };
  const saveMR = async () => {
    if (!mrForm.lines.length) return toast.error("Add at least one line");
    try { await axios.post(`${API}/inventory/requisitions`, mrForm, { withCredentials: true }); toast.success("Requisition raised"); setMrDialog(false); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const setMRStatus = async (mr_id, status) => {
    try { await axios.patch(`${API}/inventory/requisitions/${mr_id}/status`, { status }, { withCredentials: true }); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const openNewPO = () => { setPoForm({ ...emptyPO }); setPoDialog(true); };
  const convertMRtoPO = (mr) => {
    // Pre-fill the PO dialog with lines from the MR
    setPoForm({
      ...emptyPO,
      mr_id: mr.mr_id,
      lines: (mr.lines || []).map((ln) => {
        const it = items.find((i) => i.item_id === ln.item_id);
        return {
          item_id: ln.item_id,
          quantity: ln.quantity,
          unit_price: it?.unit_price || 0,
          gst_rate: it?.gst_rate || 0,
          remarks: ln.remarks || "",
        };
      }),
      notes: `Auto-created from ${mr.number}`,
    });
    setPoDialog(true);
  };
  const savePO = async () => {
    if (!poForm.vendor_id) return toast.error("Select a vendor");
    if (!poForm.lines.length) return toast.error("Add at least one line");
    try { await axios.post(`${API}/inventory/purchase-orders`, poForm, { withCredentials: true }); toast.success("PO created"); setPoDialog(false); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  // Print/Download PO as PDF (browser print-to-PDF)
  const downloadPOPdf = (po) => {
    const vendor = vendors.find((v) => v.vendor_id === po.vendor_id) || {};
    const rowsHtml = (po.lines || []).map((ln, idx) => {
      const it = items.find((i) => i.item_id === ln.item_id) || {};
      const qty = Number(ln.quantity || 0);
      const rate = Number(ln.unit_price || 0);
      const gst = Number(ln.gst_rate || 0);
      const sub = qty * rate;
      const tax = sub * gst / 100;
      const total = sub + tax;
      return `<tr>
        <td>${idx + 1}</td>
        <td>${escHtml(it.code || "")}</td>
        <td>${escHtml(it.name || "")}<div style="font-size:10px;color:#666">${escHtml(it.description || "")}</div></td>
        <td>${escHtml(it.hsn_sac || "")}</td>
        <td style="text-align:right">${qty} ${escHtml(it.unit || "")}</td>
        <td style="text-align:right">${rate.toFixed(2)}</td>
        <td style="text-align:right">${gst.toFixed(2)}%</td>
        <td style="text-align:right">${sub.toFixed(2)}</td>
        <td style="text-align:right">${tax.toFixed(2)}</td>
        <td style="text-align:right;font-weight:600">${total.toFixed(2)}</td>
      </tr>`;
    }).join("");

    const subtotal = Number(po.subtotal || 0);
    const gstTotal = Number(po.gst_total || 0);
    const grand = Number(po.grand_total || 0);

    const html = `<!doctype html><html><head><meta charset="utf-8" />
      <title>Purchase Order ${escHtml(po.number || "")}</title>
      <style>
        body{font-family:Arial,Helvetica,sans-serif;color:#111;margin:24px;font-size:12px}
        .hdr{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #111;padding-bottom:12px;margin-bottom:16px}
        .hdr .co h1{margin:0;font-size:20px}
        .hdr .co p{margin:2px 0;font-size:11px;color:#333}
        .hdr .meta{text-align:right;font-size:12px}
        h2{background:#f2f2f2;padding:6px 10px;font-size:13px;margin:14px 0 6px}
        table{width:100%;border-collapse:collapse;margin-top:8px}
        th,td{border:1px solid #ccc;padding:6px 8px;text-align:left;vertical-align:top}
        th{background:#f7f7f7;font-size:11px}
        .totals{margin-top:12px;float:right;width:280px}
        .totals td{border:none;padding:4px 8px}
        .totals .grand{font-weight:700;border-top:2px solid #111;font-size:13px}
        .signblock{margin-top:96px;display:flex;justify-content:space-between}
        .sign{width:200px;border-top:1px solid #333;padding-top:6px;text-align:center;font-size:11px}
        @media print{@page{margin:16mm}}
      </style></head><body>
      <div class="hdr">
        <div class="co">
          <h1>CM Envirosystems Pvt Ltd</h1>
          <p>18, 19 Dobaspet Industrial Area, Bengaluru - 562111</p>
          <p>www.cmenvirosystems.com</p>
        </div>
        <div class="meta">
          <strong style="font-size:15px">PURCHASE ORDER</strong><br/>
          <strong>${escHtml(po.number || "")}</strong><br/>
          Date: ${escHtml((po.created_at || "").split("T")[0])}<br/>
          Expected: ${escHtml(po.expected_date || "—")}
        </div>
      </div>

      <div style="display:flex;gap:24px">
        <div style="flex:1">
          <h2>Vendor</h2>
          <div><strong>${escHtml(vendor.name || po.vendor_name || "")}</strong></div>
          <div>${escHtml(vendor.contact_person || "")}</div>
          <div>${escHtml(vendor.address || "")}</div>
          <div>Phone: ${escHtml(vendor.phone || "")} · Email: ${escHtml(vendor.email || "")}</div>
          <div>GSTIN: ${escHtml(vendor.gstin || "—")}</div>
        </div>
        <div style="flex:1">
          <h2>Ship / Deliver To</h2>
          <div>${escHtml(po.delivery_address || DEFAULT_DELIVERY_ADDRESS)}</div>
          <div style="margin-top:8px">Payment Terms: ${escHtml(po.payment_terms || "—")}</div>
        </div>
      </div>

      <h2>Items</h2>
      <table>
        <thead><tr>
          <th>#</th><th>Code</th><th>Description</th><th>HSN</th>
          <th style="text-align:right">Qty</th><th style="text-align:right">Rate ₹</th>
          <th style="text-align:right">GST%</th><th style="text-align:right">Subtotal ₹</th>
          <th style="text-align:right">Tax ₹</th><th style="text-align:right">Total ₹</th>
        </tr></thead>
        <tbody>${rowsHtml}</tbody>
      </table>

      <table class="totals">
        <tr><td>Subtotal</td><td style="text-align:right">₹${subtotal.toFixed(2)}</td></tr>
        <tr><td>GST</td><td style="text-align:right">₹${gstTotal.toFixed(2)}</td></tr>
        <tr class="grand"><td>Grand Total</td><td style="text-align:right">₹${grand.toFixed(2)}</td></tr>
      </table>
      <div style="clear:both"></div>

      ${po.notes ? `<h2>Notes</h2><div>${escHtml(po.notes)}</div>` : ""}

      <div class="signblock">
        <div class="sign">Prepared By</div>
        <div class="sign">Authorised Signatory</div>
      </div>

      <script>window.onload=function(){setTimeout(function(){window.print();},250);};</script>
      </body></html>`;

    const w = window.open("", "_blank", "width=900,height=700");
    if (!w) { toast.error("Popup blocked — allow popups to download PDF"); return; }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  const openNewInward = () => { setInwardForm(emptyInward); setInwardPoLines([]); setInwardDialog(true); };
  const loadPoForInward = async (po_id) => {
    if (!po_id) { setInwardPoLines([]); setInwardForm({ ...emptyInward }); return; }
    try {
      const res = await axios.get(`${API}/inventory/purchase-orders/${po_id}/grn-status`, { withCredentials: true });
      const lines = (res.data.lines || []).map((r) => ({ ...r, receive_now: r.pending }));
      setInwardPoLines(lines);
      setInwardForm((f) => ({ ...f, po_id, vendor_id: res.data.vendor_id || "" }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load PO");
    }
  };
  const saveInward = async () => {
    if (!inwardForm.po_id) return toast.error("Select a Purchase Order first");
    const lines = inwardPoLines
      .filter((r) => Number(r.receive_now || 0) > 0)
      .map((r) => ({ item_id: r.item_id, quantity: Number(r.receive_now) }));
    if (!lines.length) return toast.error("Enter received quantity for at least one line");
    try {
      await axios.post(`${API}/inventory/inward`, { ...inwardForm, lines }, { withCredentials: true });
      toast.success("Inward recorded");
      setInwardDialog(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const openNewIssue = () => { setIssueForm(emptyIssue); setIssueDialog(true); };
  const saveIssue = async () => {
    if (!issueForm.lines.length) return toast.error("Add at least one line");
    try { await axios.post(`${API}/inventory/issuance`, issueForm, { withCredentials: true }); toast.success("Issued (stock updated)"); setIssueDialog(false); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-secondary" data-testid="inventory-loading"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-secondary/30" data-testid="inventory-home">
      <header className="glass-header border-b border-black/10 sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 md:px-6 h-16 max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/")} data-testid="back-to-home"><ArrowLeft size={18} className="mr-1" /> Home</Button>
            <div className="hidden sm:flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-amber-600 flex items-center justify-center"><Package size={22} weight="bold" className="text-white" /></div>
              <div><h1 className="text-lg font-bold tracking-tight">Inventory Management</h1><p className="text-xs text-muted-foreground">Full procurement workflow</p></div>
            </div>
          </div>
          <div className="text-sm text-muted-foreground">{user?.name} <Badge variant="outline" className="ml-2">{user?.inventory_role || "none"}</Badge></div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-4 md:p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="dashboard" data-testid="inv-tab-dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="items" data-testid="inv-tab-items">Items</TabsTrigger>
            <TabsTrigger value="vendors" data-testid="inv-tab-vendors">Vendors</TabsTrigger>
            <TabsTrigger value="categories" data-testid="inv-tab-categories">Categories</TabsTrigger>
            <TabsTrigger value="uoms" data-testid="inv-tab-uoms">UOMs</TabsTrigger>
            <TabsTrigger value="departments" data-testid="inv-tab-departments">Departments</TabsTrigger>
            <TabsTrigger value="mrs" data-testid="inv-tab-mrs">Requisitions</TabsTrigger>
            <TabsTrigger value="pos" data-testid="inv-tab-pos">Purchase Orders</TabsTrigger>
            <TabsTrigger value="inward" data-testid="inv-tab-inward">Inward</TabsTrigger>
            <TabsTrigger value="issuance" data-testid="inv-tab-issuance">Issuance</TabsTrigger>
            <TabsTrigger value="reports" data-testid="inv-tab-reports">Reports</TabsTrigger>
          </TabsList>

          {/* DASHBOARD */}
          <TabsContent value="dashboard" className="mt-6">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              <ClickableCard icon={<Package size={20} className="text-amber-700" />} label="Items" value={summary?.total_items ?? 0} sub={`${summary?.active_items ?? 0} active`} onClick={() => setActiveTab("items")} testid="widget-items" />
              <ClickableCard icon={<Truck size={20} className="text-indigo-600" />} label="Vendors" value={summary?.total_vendors ?? 0} sub={`${summary?.active_vendors ?? 0} active`} onClick={() => setActiveTab("vendors")} testid="widget-vendors" />
              <ClickableCard icon={<Warning size={20} className="text-destructive" />} label="Low-Stock" value={summary?.low_stock_count ?? 0} sub="items at/below ROL" highlight={summary?.low_stock_count > 0} onClick={() => setActiveTab("reports")} testid="widget-low-stock" />
              <ClickableCard icon={<ClipboardText size={20} className="text-emerald-600" />} label="Open MRs" value={summary?.open_requisitions ?? 0} sub="pending/approved" onClick={() => setActiveTab("mrs")} testid="widget-mrs" />
              <ClickableCard icon={<ShoppingCart size={20} className="text-violet-600" />} label="Open POs" value={summary?.open_purchase_orders ?? 0} sub="issued/partial" onClick={() => setActiveTab("pos")} testid="widget-pos" />
              <ClickableCard icon={<PaperPlaneTilt size={20} className="text-sky-600" />} label="Inward Entries" value={summary?.inward_entries ?? 0} sub="lifetime" onClick={() => setActiveTab("inward")} testid="widget-inward" />
              <ClickableCard icon={<Storefront size={20} className="text-rose-600" />} label="Issuances" value={summary?.issuance_entries ?? 0} sub="lifetime" onClick={() => setActiveTab("issuance")} testid="widget-issuance" />
              <ClickableCard icon={<ChartBar size={20} className="text-slate-700" />} label="Reports" value="→" sub="spend · vendor · low-stock" onClick={() => setActiveTab("reports")} testid="widget-reports" />
            </div>
          </TabsContent>

          {/* ITEMS */}
          <TabsContent value="items" className="mt-6">
            <TabHeader title="Item Master" count={items.length}>
              {canEditInventory && <><Button variant="outline" onClick={() => setBulkItemsOpen(true)} data-testid="bulk-items-btn"><UploadSimple size={14} className="mr-2" /> Bulk Upload</Button><Button onClick={openNewItem} data-testid="new-item-btn"><Plus size={14} className="mr-2" /> Add Item</Button></>}
            </TabHeader>
            <Card><CardContent className="pt-4">
              <SearchBar value={itemsSearch} onChange={setItemsSearch} placeholder="Search by code, name, category..." testid="items-search" />
              <p className="text-xs text-muted-foreground mb-2">Showing {filteredItems.length} of {items.length} items. Scroll inside the table for more.</p>
              <div className="overflow-auto border rounded-md" style={{ maxHeight: "min(65vh, 640px)" }}>
                <Table>
                  <TableHeader className="sticky top-0 bg-background z-10"><TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Category</TableHead><TableHead>Unit</TableHead><TableHead className="text-right">Stock</TableHead><TableHead className="text-right">ROL</TableHead><TableHead className="text-right">MOQ</TableHead><TableHead className="text-right">Unit ₹</TableHead><TableHead>Status</TableHead>{canEditInventory && <TableHead className="text-right">Actions</TableHead>}</TableRow></TableHeader>
                  <TableBody>
                    {filteredItems.length === 0 ? <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground py-6">{items.length === 0 ? "No items yet." : "No matches."}</TableCell></TableRow>
                    : filteredItems.map((it) => (
                      <TableRow key={it.item_id} data-testid={`item-row-${it.item_id}`}>
                        <TableCell><code className="text-xs bg-secondary px-2 py-1 rounded">{it.code}</code></TableCell>
                        <TableCell className="font-medium">{it.name}</TableCell>
                        <TableCell>{it.category || "—"}</TableCell>
                        <TableCell>{it.unit}</TableCell>
                        <TableCell className="text-right">{it.opening_stock}</TableCell>
                        <TableCell className="text-right">{it.rol}</TableCell>
                        <TableCell className="text-right">{it.moq}</TableCell>
                        <TableCell className="text-right">{it.unit_price || 0}</TableCell>
                        <TableCell>{!it.is_active ? <Badge variant="secondary">Inactive</Badge> : it.low_stock ? <Badge variant="destructive">Low</Badge> : <Badge variant="outline">OK</Badge>}</TableCell>
                        {canEditInventory && <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => openEditItem(it)}><Pencil size={14} /></Button><Button size="sm" variant="ghost" onClick={() => deleteItem(it)}><Trash size={14} className="text-destructive" /></Button></TableCell>}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent></Card>
          </TabsContent>

          {/* VENDORS */}
          <TabsContent value="vendors" className="mt-6">
            <TabHeader title="Vendor Master" count={vendors.length}>
              {canEditInventory && <><Button variant="outline" onClick={() => setBulkVendorsOpen(true)} data-testid="bulk-vendors-btn"><UploadSimple size={14} className="mr-2" /> Bulk Upload</Button><Button onClick={openNewVendor} data-testid="new-vendor-btn"><Plus size={14} className="mr-2" /> Add Vendor</Button></>}
            </TabHeader>
            <Card><CardContent className="pt-4">
              <SearchBar value={vendorsSearch} onChange={setVendorsSearch} placeholder="Search by name, email, phone..." testid="vendors-search" />
              <ScrollArea className="max-h-[60vh]">
                <Table>
                  <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Contact</TableHead><TableHead>Email/Phone</TableHead><TableHead>GSTIN</TableHead><TableHead>Terms</TableHead><TableHead>Categories</TableHead>{canEditInventory && <TableHead className="text-right">Actions</TableHead>}</TableRow></TableHeader>
                  <TableBody>
                    {filteredVendors.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">{vendors.length === 0 ? "No vendors yet." : "No matches."}</TableCell></TableRow>
                    : filteredVendors.map((v) => (
                      <TableRow key={v.vendor_id} data-testid={`vendor-row-${v.vendor_id}`}>
                        <TableCell className="font-medium">{v.name}</TableCell>
                        <TableCell>{v.contact_person || "—"}</TableCell>
                        <TableCell><div className="text-sm">{v.email || "—"}</div><div className="text-xs text-muted-foreground">{v.phone || ""}</div></TableCell>
                        <TableCell><code className="text-xs">{v.gstin || "—"}</code></TableCell>
                        <TableCell className="text-sm">{v.payment_terms || "—"}</TableCell>
                        <TableCell><div className="flex flex-wrap gap-1">{(v.categories || []).slice(0, 3).map((c, i) => <Badge key={i} variant="outline" className="text-[10px]">{c}</Badge>)}</div></TableCell>
                        {canEditInventory && <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => openEditVendor(v)}><Pencil size={14} /></Button><Button size="sm" variant="ghost" onClick={() => deleteVendor(v)}><Trash size={14} className="text-destructive" /></Button></TableCell>}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent></Card>
          </TabsContent>

          {/* CATEGORIES */}
          <TabsContent value="categories" className="mt-6">
            <TabHeader title="Category Master" count={categories.length}>{canEditInventory && <Button onClick={openNewCat} data-testid="new-category-btn"><Plus size={14} className="mr-2" /> Add Category</Button>}</TabHeader>
            <Card><CardContent className="pt-4">
              <Table>
                <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Description</TableHead><TableHead>Status</TableHead>{canEditInventory && <TableHead className="text-right">Actions</TableHead>}</TableRow></TableHeader>
                <TableBody>{categories.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">No categories yet.</TableCell></TableRow> : categories.map((c) => (
                  <TableRow key={c.category_id} data-testid={`category-row-${c.category_id}`}>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{c.description || "—"}</TableCell>
                    <TableCell>{c.is_active ? <Badge variant="outline">Active</Badge> : <Badge variant="secondary">Inactive</Badge>}</TableCell>
                    {canEditInventory && <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => openEditCat(c)}><Pencil size={14} /></Button><Button size="sm" variant="ghost" onClick={() => deleteCat(c)}><Trash size={14} className="text-destructive" /></Button></TableCell>}
                  </TableRow>
                ))}</TableBody>
              </Table>
            </CardContent></Card>
          </TabsContent>

          {/* UOMs */}
          <TabsContent value="uoms" className="mt-6">
            <TabHeader title="UOM / Unit Master" count={uoms.length}>{canEditInventory && <Button onClick={openNewUom} data-testid="new-uom-btn"><Plus size={14} className="mr-2" /> Add UOM</Button>}</TabHeader>
            <Card><CardContent className="pt-4">
              <Table>
                <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Status</TableHead>{canEditInventory && <TableHead className="text-right">Actions</TableHead>}</TableRow></TableHeader>
                <TableBody>{uoms.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">No UOMs yet.</TableCell></TableRow> : uoms.map((u) => (
                  <TableRow key={u.uom_id} data-testid={`uom-row-${u.uom_id}`}>
                    <TableCell><code className="text-xs bg-secondary px-2 py-1 rounded">{u.code}</code></TableCell>
                    <TableCell>{u.name}</TableCell>
                    <TableCell>{u.is_active ? <Badge variant="outline">Active</Badge> : <Badge variant="secondary">Inactive</Badge>}</TableCell>
                    {canEditInventory && <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => openEditUom(u)}><Pencil size={14} /></Button><Button size="sm" variant="ghost" onClick={() => deleteUom(u)}><Trash size={14} className="text-destructive" /></Button></TableCell>}
                  </TableRow>
                ))}</TableBody>
              </Table>
            </CardContent></Card>
          </TabsContent>

          {/* DEPARTMENTS */}
          <TabsContent value="departments" className="mt-6">
            <TabHeader title="Department Master" count={invDepartments.length}>{canEditInventory && <Button onClick={openNewDept} data-testid="new-dept-btn"><Plus size={14} className="mr-2" /> Add Department</Button>}</TabHeader>
            <Card><CardContent className="pt-4">
              <Table>
                <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Description</TableHead><TableHead>Status</TableHead>{canEditInventory && <TableHead className="text-right">Actions</TableHead>}</TableRow></TableHeader>
                <TableBody>{invDepartments.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">No departments yet.</TableCell></TableRow> : invDepartments.map((d) => (
                  <TableRow key={d.department_id} data-testid={`dept-row-${d.department_id}`}>
                    <TableCell className="font-medium">{d.name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{d.description || "—"}</TableCell>
                    <TableCell>{d.is_active ? <Badge variant="outline">Active</Badge> : <Badge variant="secondary">Inactive</Badge>}</TableCell>
                    {canEditInventory && <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => openEditDept(d)}><Pencil size={14} /></Button><Button size="sm" variant="ghost" onClick={() => deleteDept(d)}><Trash size={14} className="text-destructive" /></Button></TableCell>}
                  </TableRow>
                ))}</TableBody>
              </Table>
            </CardContent></Card>
          </TabsContent>

          {/* REQUISITIONS */}
          <TabsContent value="mrs" className="mt-6">
            <TabHeader title="Material Requisitions" count={mrs.length}>
              {canEditInventory && <Button variant="outline" onClick={autoCreateMRFromLowStock} disabled={autoMrPending} data-testid="auto-mr-btn"><Warning size={14} className="mr-2" /> {autoMrPending ? "Creating…" : "Auto-Create from Low-Stock"}</Button>}
              <Button onClick={openNewMR} data-testid="new-mr-btn"><Plus size={14} className="mr-2" /> Raise Requisition</Button>
            </TabHeader>
            <Card><CardContent className="pt-4">
              <Table>
                <TableHeader><TableRow><TableHead>#</TableHead><TableHead>Requester</TableHead><TableHead>Dept</TableHead><TableHead>Lines</TableHead><TableHead>Required By</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                <TableBody>{mrs.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">No requisitions yet.</TableCell></TableRow> : mrs.map((m) => (
                  <TableRow key={m.mr_id} data-testid={`mr-row-${m.mr_id}`}>
                    <TableCell><code className="text-xs bg-secondary px-2 py-1 rounded">{m.number}</code></TableCell>
                    <TableCell>{m.requester_name}</TableCell>
                    <TableCell>{m.department || "—"}</TableCell>
                    <TableCell className="text-sm">{(m.lines || []).length} items</TableCell>
                    <TableCell className="text-sm">{m.required_by || "—"}</TableCell>
                    <TableCell>
                      <Badge variant={m.status === "closed" ? "outline" : m.status === "rejected" ? "destructive" : "secondary"}>{m.status}</Badge>
                      {m.linked_po_number && <div className="text-[10px] text-muted-foreground mt-1">→ {m.linked_po_number}</div>}
                    </TableCell>
                    <TableCell className="text-right">
                      {canEditInventory && m.status === "pending" && <><Button size="sm" variant="ghost" onClick={() => setMRStatus(m.mr_id, "approved")}>Approve</Button><Button size="sm" variant="ghost" onClick={() => setMRStatus(m.mr_id, "rejected")}>Reject</Button></>}
                      {canEditInventory && (m.status === "pending" || m.status === "approved") && !m.linked_po_number && (
                        <Button size="sm" variant="outline" onClick={() => convertMRtoPO(m)} data-testid={`convert-mr-${m.mr_id}`} className="ml-1">
                          <ShoppingCart size={12} className="mr-1" /> Convert to PO
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            </CardContent></Card>
          </TabsContent>

          {/* PURCHASE ORDERS */}
          <TabsContent value="pos" className="mt-6">
            <TabHeader title="Purchase Orders" count={pos.length}>{canEditInventory && <Button onClick={openNewPO} data-testid="new-po-btn"><Plus size={14} className="mr-2" /> Create PO</Button>}</TabHeader>
            <Card><CardContent className="pt-4">
              <Table>
                <TableHeader><TableRow><TableHead>#</TableHead><TableHead>Vendor</TableHead><TableHead>Lines</TableHead><TableHead className="text-right">Total ₹</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                <TableBody>{pos.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">No POs yet.</TableCell></TableRow> : pos.map((p) => (
                  <TableRow key={p.po_id} data-testid={`po-row-${p.po_id}`}>
                    <TableCell><code className="text-xs bg-secondary px-2 py-1 rounded">{p.number}</code></TableCell>
                    <TableCell>{p.vendor_name}</TableCell>
                    <TableCell className="text-sm">{(p.lines || []).length}</TableCell>
                    <TableCell className="text-right font-medium">₹{p.grand_total?.toLocaleString?.() || 0}</TableCell>
                    <TableCell><Badge variant={p.status === "closed" ? "outline" : p.status === "cancelled" ? "destructive" : "secondary"}>{p.status}</Badge></TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => downloadPOPdf(p)} data-testid={`po-pdf-${p.po_id}`}>
                        <FilePdf size={14} className="mr-1" /> PDF
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            </CardContent></Card>
          </TabsContent>

          {/* INWARD */}
          <TabsContent value="inward" className="mt-6">
            <TabHeader title="Material Inward (GRN)" count={inward.length}>{canEditInventory && <Button onClick={openNewInward} data-testid="new-inward-btn"><Plus size={14} className="mr-2" /> New Inward</Button>}</TabHeader>
            <Card><CardContent className="pt-4">
              <Table>
                <TableHeader><TableRow><TableHead>GRN #</TableHead><TableHead>PO Ref</TableHead><TableHead>Vendor</TableHead><TableHead>Invoice</TableHead><TableHead>Item</TableHead><TableHead className="text-right">Received Qty</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
                <TableBody>{inward.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">No inward entries.</TableCell></TableRow> : inward.flatMap((r) => (r.lines || []).map((ln, i) => (
                  <TableRow key={`${r.inward_id}-${i}`} data-testid={`inward-line-${r.inward_id}-${i}`}>
                    {i === 0 ? <>
                      <TableCell rowSpan={(r.lines || []).length}><code className="text-xs bg-secondary px-2 py-1 rounded">{r.number}</code></TableCell>
                      <TableCell rowSpan={(r.lines || []).length} className="text-xs">{(pos.find((p) => p.po_id === r.po_id)?.number) || <span className="text-muted-foreground">—</span>}</TableCell>
                      <TableCell rowSpan={(r.lines || []).length}>{r.vendor_name || "—"}</TableCell>
                      <TableCell rowSpan={(r.lines || []).length} className="text-sm">{r.invoice_no || "—"}</TableCell>
                    </> : null}
                    <TableCell>
                      <div className="font-medium">{ln.item_name || "—"}</div>
                      <div className="text-xs text-muted-foreground"><code>{ln.item_code}</code></div>
                    </TableCell>
                    <TableCell className="text-right">{ln.quantity} {ln.unit}</TableCell>
                    {i === 0 ? <TableCell rowSpan={(r.lines || []).length} className="text-xs text-muted-foreground">{r.created_at?.split("T")[0]}</TableCell> : null}
                  </TableRow>
                )))}</TableBody>
              </Table>
            </CardContent></Card>
          </TabsContent>

          {/* ISSUANCE */}
          <TabsContent value="issuance" className="mt-6">
            <TabHeader title="Material Issuance" count={issuance.length}>{canEditInventory && <Button onClick={openNewIssue} data-testid="new-issue-btn"><Plus size={14} className="mr-2" /> New Issuance</Button>}</TabHeader>
            <Card><CardContent className="pt-4">
              <Table>
                <TableHeader><TableRow><TableHead>Issue Number</TableHead><TableHead>Issued To</TableHead><TableHead>Department</TableHead><TableHead>Item</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Rate ₹</TableHead><TableHead className="text-right">Value ₹</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
                <TableBody>{issuance.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-6">No issuances yet.</TableCell></TableRow> : issuance.flatMap((r) => (r.lines || []).map((ln, i) => (
                  <TableRow key={`${r.issuance_id}-${i}`} data-testid={`issue-line-${r.issuance_id}-${i}`}>
                    {i === 0 ? <>
                      <TableCell rowSpan={(r.lines || []).length}><code className="text-xs bg-secondary px-2 py-1 rounded">{r.number}</code></TableCell>
                      <TableCell rowSpan={(r.lines || []).length}>{r.issued_to_name || <span className="text-muted-foreground">—</span>}</TableCell>
                      <TableCell rowSpan={(r.lines || []).length}>{r.department || "—"}</TableCell>
                    </> : null}
                    <TableCell>
                      <div className="font-medium">{ln.item_name || "—"}</div>
                      <div className="text-xs text-muted-foreground"><code>{ln.item_code}</code></div>
                    </TableCell>
                    <TableCell className="text-right">{ln.quantity} {ln.unit}</TableCell>
                    <TableCell className="text-right">₹{(ln.rate || 0).toLocaleString()}</TableCell>
                    <TableCell className="text-right font-medium">₹{(ln.value || 0).toLocaleString()}</TableCell>
                    {i === 0 ? <TableCell rowSpan={(r.lines || []).length} className="text-xs text-muted-foreground">{r.created_at?.split("T")[0]}</TableCell> : null}
                  </TableRow>
                )))}</TableBody>
              </Table>
            </CardContent></Card>
          </TabsContent>

          {/* REPORTS */}
          <TabsContent value="reports" className="mt-6 space-y-6">
            <ReportCard title={`Stock Summary (${reports2.stock?.item_count ?? 0} items)`} total={reports2.stock?.grand_total}>
              <div className="overflow-auto" style={{ maxHeight: "50vh" }}>
                <Table>
                  <TableHeader className="sticky top-0 bg-background z-10"><TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Category</TableHead><TableHead>Unit</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Rate ₹</TableHead><TableHead className="text-right">Value ₹</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {(reports2.stock?.rows || []).length === 0 ? <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-4">No items.</TableCell></TableRow>
                    : (reports2.stock?.rows || []).map((r) => (
                      <TableRow key={r.item_id} data-testid={`stock-summary-row-${r.item_id}`}>
                        <TableCell><code className="text-xs bg-secondary px-2 py-1 rounded">{r.code}</code></TableCell>
                        <TableCell className="font-medium">{r.name} {r.low_stock && <Badge variant="destructive" className="ml-1 text-[9px]">Low</Badge>}</TableCell>
                        <TableCell>{r.category || "—"}</TableCell>
                        <TableCell>{r.unit}</TableCell>
                        <TableCell className="text-right">{r.quantity}</TableCell>
                        <TableCell className="text-right">₹{r.rate?.toLocaleString?.() || 0}</TableCell>
                        <TableCell className="text-right font-medium">₹{r.value?.toLocaleString?.() || 0}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </ReportCard>
            <div className="grid md:grid-cols-2 gap-4">
              <ReportCard title="Spend by Category" total={reports.spend?.grand_total}>
                <Table><TableHeader><TableRow><TableHead>Category</TableHead><TableHead className="text-right">Spend ₹</TableHead></TableRow></TableHeader>
                  <TableBody>{(reports.spend?.rows || []).map((r, i) => <TableRow key={i}><TableCell>{r.category}</TableCell><TableCell className="text-right">₹{r.total_spend?.toLocaleString()}</TableCell></TableRow>)}
                    {(reports.spend?.rows || []).length === 0 && <TableRow><TableCell colSpan={2} className="text-center text-muted-foreground py-4">No PO data.</TableCell></TableRow>}
                  </TableBody></Table>
              </ReportCard>
              <ReportCard title="Vendor Performance">
                <Table><TableHeader><TableRow><TableHead>Vendor</TableHead><TableHead className="text-right">POs</TableHead><TableHead className="text-right">Total ₹</TableHead></TableRow></TableHeader>
                  <TableBody>{(reports.vendor?.rows || []).map((r, i) => <TableRow key={i}><TableCell>{r.vendor_name}</TableCell><TableCell className="text-right">{r.po_count}</TableCell><TableCell className="text-right">₹{r.total_spend?.toLocaleString()}</TableCell></TableRow>)}
                    {(reports.vendor?.rows || []).length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-4">No vendor data.</TableCell></TableRow>}
                  </TableBody></Table>
              </ReportCard>
            </div>
            <ReportCard title={`Low-Stock Items (${reports.lowStock?.count ?? 0})`}>
              <Table><TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead className="text-right">Stock</TableHead><TableHead className="text-right">ROL</TableHead><TableHead className="text-right">MOQ</TableHead></TableRow></TableHeader>
                <TableBody>{(reports.lowStock?.rows || []).map((r) => <TableRow key={r.item_id}><TableCell><code className="text-xs">{r.code}</code></TableCell><TableCell>{r.name}</TableCell><TableCell className="text-right text-destructive font-medium">{r.opening_stock}</TableCell><TableCell className="text-right">{r.rol}</TableCell><TableCell className="text-right">{r.moq}</TableCell></TableRow>)}
                  {(reports.lowStock?.rows || []).length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-4">All items above ROL.</TableCell></TableRow>}
                </TableBody></Table>
            </ReportCard>
          </TabsContent>
        </Tabs>
      </div>

      <BulkImportDialog open={bulkItemsOpen} onOpenChange={setBulkItemsOpen} mode="items" onImported={load} />
      <BulkImportDialog open={bulkVendorsOpen} onOpenChange={setBulkVendorsOpen} mode="vendors" onImported={load} />

      {/* Item Dialog */}
      <Dialog open={itemDialog} onOpenChange={setItemDialog}>
        <DialogContent className="max-w-2xl" data-testid="item-dialog">
          <DialogHeader><DialogTitle>{editItemId ? "Edit Item" : "Add Item"}</DialogTitle><DialogDescription>MOQ = Minimum Order Quantity. ROL = Re-Order Level.</DialogDescription></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4 max-h-[65vh] overflow-y-auto pr-1">
            <div><Label>Code *</Label><Input value={itemForm.code} onChange={(e) => setItemForm({ ...itemForm, code: e.target.value })} data-testid="item-code" /></div>
            <div><Label>Name *</Label><Input value={itemForm.name} onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })} data-testid="item-name" /></div>
            <div>
              <Label>Category</Label>
              <Select value={itemForm.category || ""} onValueChange={(v) => setItemForm({ ...itemForm, category: v })}>
                <SelectTrigger data-testid="item-category"><SelectValue placeholder={categories.length === 0 ? "Add categories first" : "Select"} /></SelectTrigger>
                <SelectContent>{categories.filter((c) => c.is_active).map((c) => <SelectItem key={c.category_id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Unit</Label>
              <Select value={itemForm.unit || ""} onValueChange={(v) => setItemForm({ ...itemForm, unit: v })}>
                <SelectTrigger data-testid="item-unit"><SelectValue placeholder={uoms.length === 0 ? "Add UOMs first" : "Select"} /></SelectTrigger>
                <SelectContent>{uoms.filter((u) => u.is_active).map((u) => <SelectItem key={u.uom_id} value={u.code}>{u.code} — {u.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>MOQ</Label><Input type="number" value={itemForm.moq} onChange={(e) => setItemForm({ ...itemForm, moq: Number(e.target.value) })} /></div>
            <div><Label>ROL</Label><Input type="number" value={itemForm.rol} onChange={(e) => setItemForm({ ...itemForm, rol: Number(e.target.value) })} /></div>
            <div><Label>Opening Stock</Label><Input type="number" value={itemForm.opening_stock} onChange={(e) => setItemForm({ ...itemForm, opening_stock: Number(e.target.value) })} /></div>
            <div><Label>Unit Price ₹</Label><Input type="number" value={itemForm.unit_price} onChange={(e) => setItemForm({ ...itemForm, unit_price: Number(e.target.value) })} /></div>
            <div><Label>HSN / SAC</Label><Input value={itemForm.hsn_sac} onChange={(e) => setItemForm({ ...itemForm, hsn_sac: e.target.value })} /></div>
            <div><Label>GST %</Label><Input type="number" value={itemForm.gst_rate} onChange={(e) => setItemForm({ ...itemForm, gst_rate: Number(e.target.value) })} /></div>
            <div className="sm:col-span-2"><Label>Description</Label><Textarea rows={2} value={itemForm.description} onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })} /></div>
            <div className="sm:col-span-2 flex items-center gap-3"><Switch checked={itemForm.is_active} onCheckedChange={(v) => setItemForm({ ...itemForm, is_active: v })} /><Label>Active</Label></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setItemDialog(false)}>Cancel</Button><Button onClick={saveItem} data-testid="save-item-btn">{editItemId ? "Save" : "Create"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vendor Dialog */}
      <Dialog open={vendorDialog} onOpenChange={setVendorDialog}>
        <DialogContent className="max-w-2xl" data-testid="vendor-dialog">
          <DialogHeader><DialogTitle>{editVendorId ? "Edit Vendor" : "Add Vendor"}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4 max-h-[65vh] overflow-y-auto pr-1">
            <div className="sm:col-span-2"><Label>Name *</Label><Input value={vendorForm.name} onChange={(e) => setVendorForm({ ...vendorForm, name: e.target.value })} data-testid="vendor-name" /></div>
            <div><Label>Contact Person</Label><Input value={vendorForm.contact_person} onChange={(e) => setVendorForm({ ...vendorForm, contact_person: e.target.value })} /></div>
            <div><Label>Email</Label><Input value={vendorForm.email} onChange={(e) => setVendorForm({ ...vendorForm, email: e.target.value })} /></div>
            <div><Label>Phone</Label><Input value={vendorForm.phone} onChange={(e) => setVendorForm({ ...vendorForm, phone: e.target.value })} /></div>
            <div><Label>Payment Terms</Label><Input value={vendorForm.payment_terms} onChange={(e) => setVendorForm({ ...vendorForm, payment_terms: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>Address</Label><Textarea rows={2} value={vendorForm.address} onChange={(e) => setVendorForm({ ...vendorForm, address: e.target.value })} /></div>
            <div><Label>GSTIN</Label><Input value={vendorForm.gstin} onChange={(e) => setVendorForm({ ...vendorForm, gstin: e.target.value })} /></div>
            <div><Label>PAN</Label><Input value={vendorForm.pan} onChange={(e) => setVendorForm({ ...vendorForm, pan: e.target.value })} /></div>
            <div><Label>Categories (comma-separated)</Label><Input value={(vendorForm.categories || []).join(", ")} onChange={(e) => setVendorForm({ ...vendorForm, categories: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} /></div>
            <div className="sm:col-span-2 flex items-center gap-3"><Switch checked={vendorForm.is_active} onCheckedChange={(v) => setVendorForm({ ...vendorForm, is_active: v })} /><Label>Active</Label></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setVendorDialog(false)}>Cancel</Button><Button onClick={saveVendor} data-testid="save-vendor-btn">{editVendorId ? "Save" : "Create"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={catDialog} onOpenChange={setCatDialog}>
        <DialogContent data-testid="category-dialog">
          <DialogHeader><DialogTitle>{editCatId ? "Edit Category" : "Add Category"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} data-testid="cat-name" /></div>
            <div><Label>Description</Label><Textarea rows={2} value={catForm.description} onChange={(e) => setCatForm({ ...catForm, description: e.target.value })} /></div>
            <div className="flex items-center gap-3"><Switch checked={catForm.is_active} onCheckedChange={(v) => setCatForm({ ...catForm, is_active: v })} /><Label>Active</Label></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCatDialog(false)}>Cancel</Button><Button onClick={saveCat} data-testid="save-cat-btn">{editCatId ? "Save" : "Create"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* UOM Dialog */}
      <Dialog open={uomDialog} onOpenChange={setUomDialog}>
        <DialogContent data-testid="uom-dialog">
          <DialogHeader><DialogTitle>{editUomId ? "Edit UOM" : "Add UOM"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Code (PCS, BOX, KG) *</Label><Input value={uomForm.code} onChange={(e) => setUomForm({ ...uomForm, code: e.target.value })} data-testid="uom-code" /></div>
            <div><Label>Name *</Label><Input value={uomForm.name} onChange={(e) => setUomForm({ ...uomForm, name: e.target.value })} data-testid="uom-name" /></div>
            <div className="flex items-center gap-3"><Switch checked={uomForm.is_active} onCheckedChange={(v) => setUomForm({ ...uomForm, is_active: v })} /><Label>Active</Label></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setUomDialog(false)}>Cancel</Button><Button onClick={saveUom} data-testid="save-uom-btn">{editUomId ? "Save" : "Create"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Department Dialog */}
      <Dialog open={deptDialog} onOpenChange={setDeptDialog}>
        <DialogContent data-testid="dept-dialog">
          <DialogHeader><DialogTitle>{editDeptId ? "Edit Department" : "Add Department"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input value={deptForm.name} onChange={(e) => setDeptForm({ ...deptForm, name: e.target.value })} data-testid="dept-name" /></div>
            <div><Label>Description</Label><Textarea rows={2} value={deptForm.description} onChange={(e) => setDeptForm({ ...deptForm, description: e.target.value })} /></div>
            <div className="flex items-center gap-3"><Switch checked={deptForm.is_active} onCheckedChange={(v) => setDeptForm({ ...deptForm, is_active: v })} /><Label>Active</Label></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDeptDialog(false)}>Cancel</Button><Button onClick={saveDept} data-testid="save-dept-btn">{editDeptId ? "Save" : "Create"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MR Dialog */}
      <Dialog open={mrDialog} onOpenChange={setMrDialog}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="mr-dialog">
          <DialogHeader><DialogTitle>Raise Material Requisition</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-3 gap-3">
            <div><Label>Department</Label><Input value={mrForm.department} onChange={(e) => setMrForm({ ...mrForm, department: e.target.value })} /></div>
            <div><Label>Required by</Label><Input type="date" value={mrForm.required_by} onChange={(e) => setMrForm({ ...mrForm, required_by: e.target.value })} /></div>
            <div className="sm:col-span-3"><Label>Purpose</Label><Textarea rows={2} value={mrForm.purpose} onChange={(e) => setMrForm({ ...mrForm, purpose: e.target.value })} /></div>
          </div>
          <div className="mt-4"><Label className="mb-2 block">Lines</Label><LineEditorInner items={items} lines={mrForm.lines} setLines={(l) => setMrForm({ ...mrForm, lines: l })} /></div>
          <DialogFooter><Button variant="outline" onClick={() => setMrDialog(false)}>Cancel</Button><Button onClick={saveMR} data-testid="save-mr-btn">Raise Requisition</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PO Dialog */}
      <Dialog open={poDialog} onOpenChange={setPoDialog}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="po-dialog">
          <DialogHeader><DialogTitle>{poForm.mr_id ? "Create Purchase Order (from Requisition)" : "Create Purchase Order"}</DialogTitle>{poForm.mr_id && <DialogDescription>Lines pre-filled from the requisition. Adjust vendor, prices and GST as needed.</DialogDescription>}</DialogHeader>
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <Label>Vendor *</Label>
              <Select value={poForm.vendor_id} onValueChange={(v) => setPoForm({ ...poForm, vendor_id: v })}>
                <SelectTrigger data-testid="po-vendor"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                <SelectContent>{vendors.filter((v) => v.is_active).map((v) => <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Expected Date</Label><Input type="date" value={poForm.expected_date} onChange={(e) => setPoForm({ ...poForm, expected_date: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>Delivery Address</Label><Textarea rows={2} value={poForm.delivery_address} onChange={(e) => setPoForm({ ...poForm, delivery_address: e.target.value })} data-testid="po-delivery-address" /></div>
            <div><Label>Payment Terms</Label><Input value={poForm.payment_terms} onChange={(e) => setPoForm({ ...poForm, payment_terms: e.target.value })} /></div>
            <div className="sm:col-span-3"><Label>Notes</Label><Textarea rows={2} value={poForm.notes} onChange={(e) => setPoForm({ ...poForm, notes: e.target.value })} /></div>
          </div>
          <div className="mt-4"><Label className="mb-2 block">Lines</Label><LineEditorInner items={items} lines={poForm.lines} setLines={(l) => setPoForm({ ...poForm, lines: l })} withPrice withGST /></div>
          <DialogFooter><Button variant="outline" onClick={() => setPoDialog(false)}>Cancel</Button><Button onClick={savePO} data-testid="save-po-btn">Create PO</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inward Dialog */}
      <Dialog open={inwardDialog} onOpenChange={setInwardDialog}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="inward-dialog">
          <DialogHeader>
            <DialogTitle>Material Inward (GRN)</DialogTitle>
            <DialogDescription>
              GRN can only be raised against an open Purchase Order. Enter the received quantity for each PO line — partial receipts are allowed.
            </DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="sm:col-span-3">
              <Label>Against PO *</Label>
              <Select value={inwardForm.po_id || ""} onValueChange={loadPoForInward}>
                <SelectTrigger data-testid="inward-po"><SelectValue placeholder="Select a Purchase Order" /></SelectTrigger>
                <SelectContent>
                  {pos.filter((p) => p.status !== "closed" && p.status !== "cancelled").map((p) => (
                    <SelectItem key={p.po_id} value={p.po_id}>{p.number} — {p.vendor_name} (₹{p.grand_total?.toLocaleString?.() || 0})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Invoice No</Label><Input value={inwardForm.invoice_no} onChange={(e) => setInwardForm({ ...inwardForm, invoice_no: e.target.value })} data-testid="inward-invoice-no" /></div>
            <div><Label>Invoice Date</Label><Input type="date" value={inwardForm.invoice_date} onChange={(e) => setInwardForm({ ...inwardForm, invoice_date: e.target.value })} /></div>
            <div className="sm:col-span-3"><Label>Notes</Label><Textarea rows={2} value={inwardForm.notes} onChange={(e) => setInwardForm({ ...inwardForm, notes: e.target.value })} /></div>
          </div>

          {inwardForm.po_id && (
            <div className="mt-4">
              <Label className="mb-2 block">PO Lines — Enter Received Qty</Label>
              {inwardPoLines.length === 0 ? (
                <p className="text-sm text-muted-foreground py-3">This PO has no lines.</p>
              ) : (
                <div className="border rounded-md overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item</TableHead>
                        <TableHead className="text-right">Ordered</TableHead>
                        <TableHead className="text-right">Already Received</TableHead>
                        <TableHead className="text-right">Pending</TableHead>
                        <TableHead className="text-right">Receive Now</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {inwardPoLines.map((r, idx) => (
                        <TableRow key={r.item_id} data-testid={`grn-line-${r.item_id}`}>
                          <TableCell>
                            <div className="font-medium">{r.item_name}</div>
                            <div className="text-xs text-muted-foreground"><code>{r.item_code}</code></div>
                          </TableCell>
                          <TableCell className="text-right">{r.ordered} {r.unit}</TableCell>
                          <TableCell className="text-right">{r.received} {r.unit}</TableCell>
                          <TableCell className={`text-right font-medium ${r.pending > 0 ? "text-amber-700" : "text-muted-foreground"}`}>
                            {r.pending} {r.unit}
                          </TableCell>
                          <TableCell className="text-right">
                            <Input
                              type="number"
                              min="0"
                              max={r.pending}
                              value={r.receive_now}
                              disabled={r.pending <= 0}
                              onChange={(e) => {
                                const v = Math.max(0, Math.min(r.pending, Number(e.target.value)));
                                const copy = [...inwardPoLines];
                                copy[idx] = { ...copy[idx], receive_now: v };
                                setInwardPoLines(copy);
                              }}
                              className="w-24 ml-auto"
                              data-testid={`grn-receive-${r.item_id}`}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setInwardDialog(false)}>Cancel</Button>
            <Button onClick={saveInward} data-testid="save-inward-btn" disabled={!inwardForm.po_id}>Record Inward</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Issuance Dialog */}
      <Dialog open={issueDialog} onOpenChange={setIssueDialog}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="issue-dialog">
          <DialogHeader><DialogTitle>Material Issuance</DialogTitle><DialogDescription>Issue to an employee OR a department (either is optional). Will DECREMENT stock.</DialogDescription></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Issued To (Employee) — optional</Label>
              <Select value={issueForm.issued_to_user_id || "none"} onValueChange={(v) => setIssueForm({ ...issueForm, issued_to_user_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="issue-user"><SelectValue placeholder="No specific person" /></SelectTrigger>
                <SelectContent><SelectItem value="none">— No specific person —</SelectItem>{allUsers.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name} — {u.email}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Department — optional</Label>
              <Select value={issueForm.department || "none"} onValueChange={(v) => setIssueForm({ ...issueForm, department: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="issue-department"><SelectValue placeholder={invDepartments.length === 0 ? "Add departments first" : "Select department"} /></SelectTrigger>
                <SelectContent><SelectItem value="none">— None —</SelectItem>{invDepartments.filter((d) => d.is_active).map((d) => <SelectItem key={d.department_id} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2"><Label>Purpose</Label><Textarea rows={2} value={issueForm.purpose} onChange={(e) => setIssueForm({ ...issueForm, purpose: e.target.value })} /></div>
          </div>
          <div className="mt-4"><Label className="mb-2 block">Lines</Label><LineEditorInner items={items} lines={issueForm.lines} setLines={(l) => setIssueForm({ ...issueForm, lines: l })} /></div>
          <DialogFooter><Button variant="outline" onClick={() => setIssueDialog(false)}>Cancel</Button><Button onClick={saveIssue} data-testid="save-issue-btn">Issue Material</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ClickableCard({ icon, label, value, sub, highlight, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="text-left transition-transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-primary rounded-md">
      <Card className={highlight ? "border-destructive/40" : ""}>
        <CardContent className="pt-4">
          <div className="flex items-center gap-2 mb-1">{icon}<p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{label}</p></div>
          <p className="text-3xl font-bold">{value}</p>
          <p className="text-xs text-muted-foreground mt-1">{sub}</p>
        </CardContent>
      </Card>
    </button>
  );
}

function TabHeader({ title, count, children }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
      <h2 className="text-lg font-semibold">{title} <span className="text-sm font-normal text-muted-foreground">({count})</span></h2>
      <div className="flex flex-wrap items-center gap-2">{children}</div>
    </div>
  );
}

function SearchBar({ value, onChange, placeholder, testid }) {
  return (
    <div className="relative mb-3">
      <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="pl-9" data-testid={testid} />
    </div>
  );
}

function ReportCard({ title, total, children }) {
  return (
    <Card><CardContent className="pt-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold">{title}</h3>
        {total != null && <Badge variant="outline">Total ₹{total?.toLocaleString?.() || 0}</Badge>}
      </div>
      {children}
    </CardContent></Card>
  );
}

function LineEditorInner({ items, lines, setLines, withPrice = false, withGST = false }) {
  const addLine = () => setLines([...lines, { item_id: "", quantity: 1, unit_price: 0, gst_rate: 0 }]);
  const updateLine = (idx, patch) => {
    const copy = [...lines];
    copy[idx] = { ...copy[idx], ...patch };
    setLines(copy);
  };
  const removeLine = (idx) => setLines(lines.filter((_, i) => i !== idx));

  const grandSubtotal = lines.reduce((s, ln) => s + (Number(ln.quantity || 0) * Number(ln.unit_price || 0)), 0);
  const grandGST = lines.reduce((s, ln) => s + (Number(ln.quantity || 0) * Number(ln.unit_price || 0) * Number(ln.gst_rate || 0) / 100), 0);
  const grandTotal = grandSubtotal + grandGST;

  return (
    <div className="space-y-2" data-testid="line-editor">
      <div className="max-h-[40vh] overflow-y-auto pr-1 space-y-2 border rounded-md p-2 bg-secondary/20">
        {lines.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-3">No lines yet — click Add line.</p>
        )}
        {lines.map((ln, idx) => {
          const it = items.find((i) => i.item_id === ln.item_id);
          const qty = Number(ln.quantity || 0);
          const rate = Number(ln.unit_price || 0);
          const gst = Number(ln.gst_rate || 0);
          const lineTotal = qty * rate * (1 + gst / 100);
          return (
            <div key={idx} className="flex flex-wrap gap-2 items-center bg-background p-2 rounded border">
              <span className="text-xs text-muted-foreground w-6 text-right">{idx + 1}.</span>
              <div className="flex-1 min-w-[220px]">
                <ItemPicker
                  items={items}
                  value={ln.item_id}
                  onChange={(v) => {
                    const picked = items.find((i) => i.item_id === v);
                    updateLine(idx, {
                      item_id: v,
                      unit_price: withPrice ? (picked?.unit_price || 0) : ln.unit_price,
                      gst_rate: withGST ? (picked?.gst_rate || 0) : ln.gst_rate,
                    });
                  }}
                  testid={`line-item-${idx}`}
                />
                {it && (
                  <div className="text-[10px] text-muted-foreground mt-1">
                    Stock: {it.opening_stock} {it.unit} · Unit ₹{it.unit_price || 0}
                  </div>
                )}
              </div>
              <div>
                <Input
                  type="number"
                  value={ln.quantity}
                  className="w-24"
                  onChange={(e) => updateLine(idx, { quantity: Number(e.target.value) })}
                  placeholder="Qty"
                  data-testid={`line-qty-${idx}`}
                />
              </div>
              {withPrice && (
                <Input
                  type="number"
                  value={ln.unit_price ?? 0}
                  className="w-28"
                  onChange={(e) => updateLine(idx, { unit_price: Number(e.target.value) })}
                  placeholder="Rate ₹"
                  data-testid={`line-price-${idx}`}
                />
              )}
              {withGST && (
                <Input
                  type="number"
                  value={ln.gst_rate ?? 0}
                  className="w-20"
                  onChange={(e) => updateLine(idx, { gst_rate: Number(e.target.value) })}
                  placeholder="GST%"
                  data-testid={`line-gst-${idx}`}
                />
              )}
              {withPrice && (
                <div className="w-28 text-right text-sm font-medium" data-testid={`line-total-${idx}`}>
                  ₹{lineTotal.toFixed(2)}
                </div>
              )}
              <Button size="sm" variant="ghost" onClick={() => removeLine(idx)} data-testid={`line-remove-${idx}`}>
                <Trash size={14} className="text-destructive" />
              </Button>
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-between">
        <Button size="sm" variant="outline" onClick={addLine} data-testid="add-line-btn">
          <Plus size={14} className="mr-1" /> Add line
        </Button>
        {withPrice && lines.length > 0 && (
          <div className="text-sm space-x-4" data-testid="line-editor-totals">
            <span>Subtotal <strong>₹{grandSubtotal.toFixed(2)}</strong></span>
            {withGST && <span>GST <strong>₹{grandGST.toFixed(2)}</strong></span>}
            <span>Grand Total <strong>₹{grandTotal.toFixed(2)}</strong></span>
          </div>
        )}
      </div>
    </div>
  );
}
